import manifestJson from './manifest.json';
// Substrate-generated CUSTOMER manifest fragment (#226 / consolidation Phase 3).
// Mirrored from `henoyo-substrate/docs/everest-v2/generated/manifest-fragment-customer.json`
// by `content-source/sync.sh` on every sync. Tracked (committed) so fresh-clone
// builds succeed without first running sync — matches the everest-docs Phase 2
// model. Diffs in git status after sync are intentional signal that substrate's
// customer-allowlisted schema contract has shifted.
import fragmentCustomerJson from './manifest-fragment-customer.json';
import { applyGroupTitleOverrides, GROUP_TITLE_OVERRIDES } from './groupTitleOverrides';

export interface ManifestTab {
  id: string;
  label: string;
  source: string;
}

export interface ManifestDocBase {
  slug: string;
  title: string;
  subtitle?: string;
  owner?: string;
  lastReviewed?: string;
  status?: 'active' | 'draft' | 'archived' | 'shipped';
}

export interface ManifestSingleDoc extends ManifestDocBase {
  source: string;
  tabs?: undefined;
}

export interface ManifestTabbedDoc extends ManifestDocBase {
  source?: undefined;
  defaultTab: string;
  tabs: ManifestTab[];
}

export type ManifestDoc = ManifestSingleDoc | ManifestTabbedDoc;

export interface ManifestGroup {
  id: string;
  title: string;
  subtitle?: string;
  kind: 'single' | 'tabbed';
  docs: ManifestDoc[];
}

export interface ManifestSite {
  title: string;
  tagline: string;
  watermark: string;
}

export interface Manifest {
  version: number;
  site: ManifestSite;
  groups: ManifestGroup[];
}

/**
 * Substrate-generated CUSTOMER manifest fragment shape (#186 / Phase 1).
 * Pinned to substrate's `MANIFEST_FRAGMENT_SCHEMA_VERSION` constant in
 * `scripts/gen_schema_artifacts.py`; bump in lock-step if the fragment
 * contract changes.
 */
interface ManifestFragment {
  manifest_fragment_schema_version?: number;
  schema_version?: number;
  audience?: string;
  groups?: ManifestGroup[];
}

const EXPECTED_FRAGMENT_SCHEMA_VERSION = 1;
const EXPECTED_FRAGMENT_AUDIENCE = 'customer';

const fragmentCustomer = fragmentCustomerJson as ManifestFragment;
if (
  fragmentCustomer.manifest_fragment_schema_version !== undefined &&
  fragmentCustomer.manifest_fragment_schema_version !== EXPECTED_FRAGMENT_SCHEMA_VERSION
) {
  throw new Error(
    `manifest: customer fragment schema version mismatch — expected ` +
      `${EXPECTED_FRAGMENT_SCHEMA_VERSION}, got ` +
      `${fragmentCustomer.manifest_fragment_schema_version}.`,
  );
}
if (
  fragmentCustomer.audience !== undefined &&
  fragmentCustomer.audience !== EXPECTED_FRAGMENT_AUDIENCE
) {
  // Audience guard — gpt55 PRE-CODE catch on #226. A misrouted
  // internal fragment must not be ingested by the customer site.
  throw new Error(
    `manifest: customer fragment audience mismatch — expected ` +
      `"${EXPECTED_FRAGMENT_AUDIENCE}", got ` +
      `"${fragmentCustomer.audience}". Refusing to load — the henoyo-docs ` +
      `customer site must never ingest internal-audience fragments.`,
  );
}
const fragmentGroups: ManifestGroup[] = fragmentCustomer.groups ?? [];

/**
 * Validate no duplicate group IDs and no duplicate (group, slug) routes
 * — gpt55 PRE-CODE catch on #226 (Phase 3 mirrors Phase 2's pattern).
 * Throw at module-load so a misconfigured manifest fails the build
 * rather than producing silent routing collisions at runtime.
 */
function _validateManifest(groups: ManifestGroup[]): void {
  const seenGroups = new Set<string>();
  for (const g of groups) {
    if (seenGroups.has(g.id)) {
      throw new Error(`manifest: duplicate group id "${g.id}"`);
    }
    seenGroups.add(g.id);
    const seenSlugs = new Set<string>();
    for (const d of g.docs) {
      if (seenSlugs.has(d.slug)) {
        throw new Error(
          `manifest: duplicate doc slug "${d.slug}" in group "${g.id}"`,
        );
      }
      seenSlugs.add(d.slug);
    }
  }
}

const mergedGroups: ManifestGroup[] = applyGroupTitleOverrides([
  ...(manifestJson.groups as unknown as ManifestGroup[]),
  ...fragmentGroups,
]);

// Dead-override guard (substrate#269): if substrate ever renames a group id
// that has an entry in GROUP_TITLE_OVERRIDES, the override silently becomes
// dead code. Fail at module load instead.
for (const id of Object.keys(GROUP_TITLE_OVERRIDES)) {
  if (!mergedGroups.some((g) => g.id === id)) {
    throw new Error(
      `manifest: GROUP_TITLE_OVERRIDES targets unknown group id "${id}" — ` +
        `substrate likely renamed the group; update src/config/groupTitleOverrides.ts.`,
    );
  }
}
_validateManifest(mergedGroups);

export const manifest: Manifest = {
  ...(manifestJson as unknown as Manifest),
  groups: mergedGroups,
};
export const groups = manifest.groups;

export function findDoc(
  groupId: string,
  slug: string,
): { group: ManifestGroup; doc: ManifestDoc } | undefined {
  const group = manifest.groups.find((g) => g.id === groupId);
  if (!group) return undefined;
  const doc = group.docs.find((d) => d.slug === slug);
  if (!doc) return undefined;
  return { group, doc };
}

export function isTabbed(doc: ManifestDoc): doc is ManifestTabbedDoc {
  return Array.isArray((doc as ManifestTabbedDoc).tabs);
}

/** Iterate every "renderable unit": single doc OR a tab inside a tabbed doc. */
export interface RenderableUnit {
  group: ManifestGroup;
  doc: ManifestDoc;
  /** undefined when doc is single-pane */
  tab?: ManifestTab;
  /** Source markdown path relative to specs/ root */
  source: string;
  /** Output path relative to functions/content/ — without .html */
  outRel: string;
}

export function renderableUnits(): RenderableUnit[] {
  const out: RenderableUnit[] = [];
  for (const group of manifest.groups) {
    for (const doc of group.docs) {
      if (isTabbed(doc)) {
        for (const tab of doc.tabs) {
          out.push({
            group,
            doc,
            tab,
            source: tab.source,
            outRel: `${group.id}/${doc.slug}/${tab.id}`,
          });
        }
      } else {
        out.push({
          group,
          doc,
          source: doc.source,
          outRel: `${group.id}/${doc.slug}`,
        });
      }
    }
  }
  return out;
}

/**
 * Flat doc-level order across all groups (one entry per doc, NOT per tab).
 * Used by DocPrevNext so navigation goes doc-to-doc, not tab-to-tab — per
 * reviewer feedback: jumping between tabs of the same initiative would feel
 * like noise; users want "next document" semantics.
 */
export interface FlatDoc {
  groupId: string;
  slug: string;
  title: string;
}

export function flatDocOrder(): FlatDoc[] {
  const out: FlatDoc[] = [];
  for (const group of manifest.groups) {
    for (const doc of group.docs) {
      out.push({ groupId: group.id, slug: doc.slug, title: doc.title });
    }
  }
  return out;
}

/**
 * Resolve the URL slug+tab for a (groupId, slug) target.
 * For a tabbed doc, returns its defaultTab. For a single doc, tab is undefined.
 */
export function defaultUrlFor(groupId: string, slug: string): string {
  const found = findDoc(groupId, slug);
  if (!found) return `/d/${groupId}/${slug}`;
  if (isTabbed(found.doc)) {
    return `/d/${groupId}/${slug}/${found.doc.defaultTab}`;
  }
  return `/d/${groupId}/${slug}`;
}
